import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  createInvoiceCustomer,
  createInvoicePreview,
  loadInvoiceBootstrap,
  sendInvoiceToCustomer,
} from "../../api/invoicing";

const steps = ["Klant", "Factuur", "Voorbeeld", "Versturen"];

const customerLabel = (customer) => {
  if (!customer) {
    return "";
  }

  const name = customer.companyName || [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return customer.email ? `${name} (${customer.email})` : name;
};

const initialNewCustomer = {
  customerType: "company",
  name: "",
  email: "",
  phone: "",
  streetAndNumber: "",
  postalCode: "",
  city: "",
  chamberOfCommerce: "",
  vatNumber: "",
};

const initialInvoiceForm = {
  description: "",
  price: "",
  quantity: "1",
  taxRateId: "",
  pricesAreInclTax: true,
};

const InvoiceWizard = ({ pin }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [contacts, setContacts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [customerMode, setCustomerMode] = useState("existing");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState(initialNewCustomer);
  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm);
  const [preview, setPreview] = useState(null);
  const [previewDirty, setPreviewDirty] = useState(false);

  useEffect(() => {
    return () => {
      if (preview?.pdfUrl) {
        URL.revokeObjectURL(preview.pdfUrl);
      }
    };
  }, [preview]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await loadInvoiceBootstrap(pin);
        setContacts(data.contacts || []);
        setTaxRates(data.taxRates || []);

        const preferredRate = (data.taxRates || []).find((rate) => Number(rate.percentage) === 21) || data.taxRates?.[0];
        if (preferredRate) {
          setInvoiceForm((current) => ({ ...current, taxRateId: preferredRate.id }));
        }
      } catch (loadError) {
        setError(loadError.message || "Factuurgegevens laden is niet gelukt.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [pin]);

  const selectedCustomerName = useMemo(() => customerLabel(selectedCustomer), [selectedCustomer]);

  const invalidatePreview = () => {
    if (preview) {
      setPreviewDirty(true);
    }
  };

  const handleNewCustomerChange = (field, value) => {
    setNewCustomer((current) => ({ ...current, [field]: value }));
  };

  const handleInvoiceFormChange = (field, value) => {
    setInvoiceForm((current) => ({ ...current, [field]: value }));
    invalidatePreview();
  };

  const ensureCustomer = async () => {
    if (customerMode === "existing") {
      if (!selectedCustomer) {
        throw new Error("Kies eerst een bestaande klant.");
      }

      return selectedCustomer;
    }

    if (!newCustomer.name.trim() || !newCustomer.email.trim()) {
      throw new Error("Vul eerst naam en e-mailadres van de nieuwe klant in.");
    }

    const createdCustomer = await createInvoiceCustomer({
      pin,
      customerType: newCustomer.customerType,
      name: newCustomer.name,
      email: newCustomer.email,
      phone: newCustomer.phone,
      streetAndNumber: newCustomer.streetAndNumber,
      postalCode: newCustomer.postalCode,
      city: newCustomer.city,
      chamberOfCommerce: newCustomer.chamberOfCommerce,
      vatNumber: newCustomer.vatNumber,
    });

    setContacts((current) => [createdCustomer, ...current]);
    setSelectedCustomer(createdCustomer);
    return createdCustomer;
  };

  const handleNext = async () => {
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (activeStep === 0) {
        await ensureCustomer();
        setActiveStep(1);
      } else if (activeStep === 1) {
        if (!invoiceForm.description.trim() || !invoiceForm.price.trim()) {
          throw new Error("Vul eerst omschrijving en prijs in.");
        }

        setActiveStep(2);
      } else if (activeStep === 2) {
        const customer = await ensureCustomer();
        const previewResult = await createInvoicePreview({
          pin,
          contactId: customer.id,
          description: invoiceForm.description,
          price: invoiceForm.price,
          quantity: invoiceForm.quantity || "1",
          taxRateId: invoiceForm.taxRateId,
          pricesAreInclTax: invoiceForm.pricesAreInclTax,
          replaceInvoiceId: preview?.invoiceId || null,
        });

        const pdfBytes = Uint8Array.from(atob(previewResult.pdfBase64), (character) => character.charCodeAt(0));
        const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        if (preview?.pdfUrl) {
          URL.revokeObjectURL(preview.pdfUrl);
        }

        setPreview({
          ...previewResult,
          pdfUrl,
        });
        setPreviewDirty(false);
        setActiveStep(3);
      }
    } catch (nextError) {
      setError(nextError.message || "Doorgaan is niet gelukt.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!preview?.invoiceId) {
      setError("Maak eerst een factuurvoorbeeld.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      await sendInvoiceToCustomer({
        pin,
        invoiceId: preview.invoiceId,
      });

      setSuccess(`Factuur voor ${preview.contactName || selectedCustomerName} is verstuurd.`);
      setActiveStep(0);
      setCustomerMode("existing");
      setSelectedCustomer(null);
      setNewCustomer(initialNewCustomer);
      setInvoiceForm(initialInvoiceForm);
      if (preview?.pdfUrl) {
        URL.revokeObjectURL(preview.pdfUrl);
      }
      setPreview(null);
      setPreviewDirty(false);
    } catch (sendError) {
      setError(sendError.message || "Factuur versturen is niet gelukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
              Factuur Wizard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Maak een conceptfactuur in stappen, bekijk eerst het voorbeeld en verstuur daarna pas de echte factuur.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step) => (
              <Step key={step}>
                <StepLabel>{step}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeStep === 0 ? (
                <Stack spacing={2.5}>
                  <ToggleButtonGroup
                    exclusive
                    value={customerMode}
                    onChange={(_, value) => {
                      if (value) {
                        setCustomerMode(value);
                      }
                    }}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    <ToggleButton value="existing">Bestaande klant</ToggleButton>
                    <ToggleButton value="new">Nieuwe klant</ToggleButton>
                  </ToggleButtonGroup>

                  {customerMode === "existing" ? (
                    <Autocomplete
                      options={contacts}
                      value={selectedCustomer}
                      onChange={(_, value) => setSelectedCustomer(value)}
                      getOptionLabel={customerLabel}
                      renderInput={(params) => <TextField {...params} label="Kies klant" />}
                    />
                  ) : (
                    <Stack spacing={2}>
                      <ToggleButtonGroup
                        exclusive
                        value={newCustomer.customerType}
                        onChange={(_, value) => {
                          if (value) {
                            handleNewCustomerChange("customerType", value);
                          }
                        }}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        <ToggleButton value="company">Bedrijf</ToggleButton>
                        <ToggleButton value="private">Particulier</ToggleButton>
                      </ToggleButtonGroup>
                      <TextField
                        label={newCustomer.customerType === "company" ? "Bedrijfsnaam" : "Naam"}
                        value={newCustomer.name}
                        onChange={(event) => handleNewCustomerChange("name", event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="E-mailadres"
                        type="email"
                        value={newCustomer.email}
                        onChange={(event) => handleNewCustomerChange("email", event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="Telefoon (optioneel)"
                        value={newCustomer.phone}
                        onChange={(event) => handleNewCustomerChange("phone", event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="Straat en huisnummer"
                        value={newCustomer.streetAndNumber}
                        onChange={(event) => handleNewCustomerChange("streetAndNumber", event.target.value)}
                        fullWidth
                      />
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <TextField
                          label="Postcode"
                          value={newCustomer.postalCode}
                          onChange={(event) => handleNewCustomerChange("postalCode", event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Plaats"
                          value={newCustomer.city}
                          onChange={(event) => handleNewCustomerChange("city", event.target.value)}
                          fullWidth
                        />
                      </Box>
                      {newCustomer.customerType === "company" ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                          <TextField
                            label="KVK-nummer (optioneel)"
                            value={newCustomer.chamberOfCommerce}
                            onChange={(event) => handleNewCustomerChange("chamberOfCommerce", event.target.value)}
                            fullWidth
                          />
                          <TextField
                            label="BTW-nummer (optioneel)"
                            value={newCustomer.vatNumber}
                            onChange={(event) => handleNewCustomerChange("vatNumber", event.target.value)}
                            fullWidth
                          />
                        </Box>
                      ) : null}
                    </Stack>
                  )}
                </Stack>
              ) : null}

              {activeStep === 1 ? (
                <Stack spacing={2}>
                  <TextField
                    label="Omschrijving van de klus"
                    value={invoiceForm.description}
                    onChange={(event) => handleInvoiceFormChange("description", event.target.value)}
                    multiline
                    minRows={5}
                    helperText="Meerdere regels zijn prima. Druk gewoon op enter voor een nieuwe regel."
                    fullWidth
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      label="Prijs"
                      value={invoiceForm.price}
                      onChange={(event) => handleInvoiceFormChange("price", event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Aantal"
                      value={invoiceForm.quantity}
                      onChange={(event) => handleInvoiceFormChange("quantity", event.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      select
                      label="BTW-tarief"
                      value={invoiceForm.taxRateId}
                      onChange={(event) => handleInvoiceFormChange("taxRateId", event.target.value)}
                      fullWidth
                    >
                      {taxRates.map((rate) => (
                        <MenuItem key={rate.id} value={rate.id}>
                          {rate.name} {rate.percentage != null ? `(${rate.percentage}%)` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ToggleButtonGroup
                      exclusive
                      value={invoiceForm.pricesAreInclTax ? "incl" : "excl"}
                      onChange={(_, value) => {
                        if (value) {
                          handleInvoiceFormChange("pricesAreInclTax", value === "incl");
                        }
                      }}
                      sx={{ alignSelf: "stretch" }}
                    >
                      <ToggleButton value="incl" sx={{ flex: 1 }}>
                        Incl. btw
                      </ToggleButton>
                      <ToggleButton value="excl" sx={{ flex: 1 }}>
                        Excl. btw
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Stack>
              ) : null}

              {activeStep === 2 ? (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Controleer de gegevens en klik op <strong>Maak voorbeeld</strong>. Dat maakt in Moneybird een conceptfactuur aan.
                  </Alert>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Klant
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {selectedCustomerName || newCustomer.name}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Omschrijving
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {invoiceForm.description}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body1">
                        Prijs: {invoiceForm.price}
                      </Typography>
                      <Typography variant="body1">
                        Aantal: {invoiceForm.quantity}
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              ) : null}

              {activeStep === 3 ? (
                <Stack spacing={2}>
                  {previewDirty ? (
                    <Alert severity="warning">
                      De gegevens zijn aangepast sinds het laatste voorbeeld. Maak eerst opnieuw een voorbeeld om de laatste versie te versturen.
                    </Alert>
                  ) : null}
                  {preview ? (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" }, gap: 2 }}>
                        <Box
                          sx={{
                            width: "100%",
                            minHeight: 620,
                            border: "1px solid rgba(36,53,45,0.12)",
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            component="object"
                            data={preview.pdfUrl}
                            type="application/pdf"
                            aria-label="Factuurvoorbeeld"
                            sx={{
                              display: "block",
                              width: "100%",
                              minHeight: 620,
                              border: 0,
                            }}
                          >
                            <Stack spacing={2} sx={{ p: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                Deze telefoon opent PDF's niet direct in de pagina. Gebruik de knop hieronder om de factuur toch te bekijken.
                              </Typography>
                              <Button
                                variant="outlined"
                                href={preview.pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Factuur openen
                              </Button>
                            </Stack>
                          </Box>
                        </Box>
                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                                Voorbeeld klaar
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Klant: {preview.contactName}
                              </Typography>
                              {preview.moneybirdInvoiceId ? (
                                <Typography variant="body2" color="text.secondary">
                                  Factuurnummer: {preview.moneybirdInvoiceId}
                                </Typography>
                              ) : null}
                              <Typography variant="body2" color="text.secondary">
                                Totaal excl. btw: {preview.totalPriceExclTax}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Totaal incl. btw: {preview.totalPriceInclTax}
                              </Typography>
                              <Button
                                variant="outlined"
                                href={preview.pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open apart
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              ) : null}

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Button
                  disabled={activeStep === 0 || submitting}
                  onClick={() => setActiveStep((current) => Math.max(current - 1, 0))}
                >
                  Terug
                </Button>

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {activeStep < 3 ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleNext}
                      disabled={submitting}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : activeStep === 0 ? (
                          <PersonAddAlt1Icon />
                        ) : activeStep === 1 ? (
                          <DescriptionOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )
                      }
                    >
                      {activeStep === 0 ? "Ga verder" : activeStep === 1 ? "Naar voorbeeld" : "Maak voorbeeld"}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleSendInvoice}
                      disabled={submitting || !preview || previewDirty}
                      startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendOutlinedIcon />}
                    >
                      Verstuur factuur
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default InvoiceWizard;
